/**
 * React Query Configuration
 * Centralized configuration for TanStack Query (React Query)
 * Following Bulletproof React pattern for API layer organization
 *
 * @see https://tanstack.com/query/latest/docs/react/guides/important-defaults
 */

import { QueryClient, DefaultOptions, UseMutationOptions } from '@tanstack/react-query';

/**
 * Default options for all queries
 * These can be overridden per-query as needed
 */
const queryConfig: DefaultOptions = {
  queries: {
    // Stale time: how long until data is considered stale
    // 5 minutes - balances fresh data with reduced network requests
    staleTime: 1000 * 60 * 5,

    // Garbage collection time: how long unused data stays in cache
    // 30 minutes - provides good cache persistence
    gcTime: 1000 * 60 * 30,

    // Retry failed requests once
    // Helps with temporary network issues without excessive retries
    retry: 1,

    // Refetch on window focus
    // Keeps data fresh when user returns to the app
    refetchOnWindowFocus: true,

    // Refetch on reconnect
    // Ensures data is fresh after network reconnection
    refetchOnReconnect: true,
  },
  mutations: {
    // Retry failed mutations once
    // Provides resilience for temporary failures
    retry: 1,
  },
};

/**
 * QueryClient instance
 * Single source of truth for React Query configuration
 * Import and use this instance throughout the application
 */
export const queryClient = new QueryClient({
  defaultOptions: queryConfig,
});

/**
 * Helper type to extract return type from async functions
 * Useful for typing React Query responses
 */
export type ApiFnReturnType<FnType extends (...args: unknown[]) => Promise<unknown>> =
  Awaited<ReturnType<FnType>>;

/**
 * Type for custom query configuration
 * Omits queryKey and queryFn to prevent overriding
 * Uses Partial to allow overriding only specific options
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type QueryConfig<T extends (...args: any[]) => any> = Partial<
  Omit<ReturnType<T>, 'queryKey' | 'queryFn'>
>;

/**
 * Type for custom mutation configuration
 * Provides type-safe mutation options
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type MutationConfig<MutationFnType extends (...args: any[]) => Promise<any>> =
  UseMutationOptions<Awaited<ReturnType<MutationFnType>>, Error, Parameters<MutationFnType>[0]>;
